var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import CheckIcon from 'images/v5/icons/check.svg';
import EditSvg from 'images/v5/icons/edit-02.svg';
import TrashSvg from 'images/v5/icons/trash-03.svg';
import Button from 'layout/atoms/Button/Button';
import Dropdown from 'layout/atoms/Dropdown/Dropdown';
import Loader from 'layout/atoms/Loader/Loader';
import { Cell, Row } from 'layout/atoms/TableGrid';
import EmptyState from 'layout/molecules/EmptyState/EmptyState';
import Grid from 'molecules/Grid/Grid';
import { openModal } from 'store/layoutSlice';
import { fetchSizes, removeSize, resetSizes, selectSizes, selectSizesErrors, selectSizesPageInfo, selectSizesRefetchFlags, selectSizesStatus, setDefaultSizeStandard, } from 'store/parameters/sizesSlice';
import ParametersTable from '../shared/ParametersTable';
function Sizes() {
    const dispatch = useDispatch();
    const { t } = useTranslation(['specification', 'common']);
    const sizes = useSelector(selectSizes);
    const pageInfo = useSelector(selectSizesPageInfo);
    const status = useSelector(selectSizesStatus);
    const errors = useSelector(selectSizesErrors);
    const refetchFlags = useSelector(selectSizesRefetchFlags);
    const [standardBeingUpdated, setStandardBeingUpdated] = useState(null);
    const fetchMore = () => {
        if ((pageInfo === null || pageInfo === void 0 ? void 0 : pageInfo.hasNextPage) && status.fetch !== 'loading') {
            dispatch(fetchSizes());
        }
    };
    useEffect(() => {
        if (status.fetch === 'idle' || refetchFlags.fetch) {
            dispatch(fetchSizes());
        }
    }, [dispatch, status.fetch, refetchFlags.fetch]);
    const handleDelete = (sizeId) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        const removedWasDefault = (_a = sizes.find((size) => size.id === sizeId)) === null || _a === void 0 ? void 0 : _a.isDefault;
        yield dispatch(removeSize(sizeId));
        // Removing the default makes the backend promote another standard. Reload rather
        // than guessing which one, so the badge never lies about the current default.
        if (removedWasDefault) {
            dispatch(resetSizes());
        }
    });
    const handleSetDefault = (sizeId) => __awaiter(this, void 0, void 0, function* () {
        setStandardBeingUpdated(sizeId);
        try {
            yield dispatch(setDefaultSizeStandard(sizeId));
        }
        finally {
            setStandardBeingUpdated(null);
        }
    });
    if (status.fetch === 'loading' || status.fetch === 'idle') {
        return React.createElement(Loader, null);
    }
    if (status.fetch === 'failed') {
        return React.createElement("div", null, errors.fetch);
    }
    const hasSizes = sizes.length > 0;
    return (React.createElement(Grid, { gap: 24 },
        React.createElement("div", { style: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end' } },
            React.createElement(Button, { color: "primary", onClick: () => dispatch(openModal({ name: 'sizeCreator' })) }, t('add_size'))),
        hasSizes ? (React.createElement(ParametersTable, { columns: [
                ['320px', '1fr'],
                ['72px', 'auto'],
            ], headers: [{ content: t('common:name') }, { content: null }], dataLength: sizes.length, hasMore: pageInfo === null || pageInfo === void 0 ? void 0 : pageInfo.hasNextPage, fetchMore: fetchMore }, sizes.map((size) => (React.createElement(Row, { key: size.id },
            React.createElement(Cell, null,
                React.createElement("span", null, size.name),
                size.isDefault && (React.createElement("span", { style: {
                        marginLeft: 8,
                        padding: '2px 8px',
                        borderRadius: 999,
                        backgroundColor: '#E6F4FF',
                        color: '#1C7ED6',
                        fontSize: 12,
                        fontWeight: 500,
                        display: 'inline-block',
                    } }, t('specification:default_size_standard_current')))),
            React.createElement(Cell, null,
                React.createElement(Dropdown, null,
                    size.isActive !== false && (React.createElement(Button, { align: "left", theme: "ghost", leftIcon: React.createElement(CheckIcon, null), loading: standardBeingUpdated === size.id && status.setDefault === 'loading', disabled: size.isDefault ||
                            (status.setDefault === 'loading' && standardBeingUpdated !== size.id), onClick: () => handleSetDefault(size.id) }, size.isDefault
                        ? t('specification:default_size_standard_current')
                        : t('specification:set_default_size_standard'))),
                    (size === null || size === void 0 ? void 0 : size.canNameBeUpdated) && (React.createElement(Button, { align: "left", theme: "ghost", leftIcon: React.createElement(EditSvg, null), onClick: () => dispatch(openModal({ name: 'sizeEditor', id: size.id })) }, t('common:edit'))),
                    (size === null || size === void 0 ? void 0 : size.canBeRemoved) && (React.createElement(Button, { align: "left", theme: "ghost", color: "danger", leftIcon: React.createElement(TrashSvg, null), onClick: () => handleDelete(size.id) }, t('common:delete')))))))))) : (React.createElement(EmptyState, null))));
}
export default memo(Sizes);
